import { useFormikContext } from "formik";
import { DebounceSettings, ThrottleSettings, debounce, throttle } from "lodash-es";
import { DependencyList, useCallback, useRef } from "react";

import { TypeHelper } from "@/common/helpers/type";
import { useEffectWithDeepCompareIf } from "@/common/hooks/effect/useEffectWithDeepCompareIf";
import { useLatestRef } from "@/common/hooks/ref/useLatestRef";

type DebounceOptions = { wait: number | undefined; options?: DebounceSettings };
type ThrottleOptions = { wait: number | undefined; options?: ThrottleSettings };

interface ComputeParams<TValues> {
  values: TValues;
  prevValues: TValues | null;
  /** Whether the first compute was run. First time it runs with initial values. */
  isInitialRun: boolean;
}

export interface FormikComputedFieldProps<TValues, TComputed> {
  /** If `false`, compute func won't be called.
    @default true
 */
  isEnabled?: boolean | ((params: ComputeParams<TValues>) => boolean);
  /** If not set, formik context is used. */
  values?: TValues;
  /** List of dependencies which trigger recalculation of the computed field value.
   * NB: don't include callbacks in deps as they can be new functions every time.
   */
  deps?: DependencyList;
  debouncedDeps?: {
    deps: DependencyList;
  } & DebounceOptions;
  throttledDeps?: {
    deps: DependencyList;
  } & ThrottleOptions;
  /** Function that calculates the new value. Memorized once. */
  compute: (params: ComputeParams<TValues>) => TComputed | Promise<TComputed>;
  /** Handle new computed value. Memorized once.*/
  onComputed?: (computed: TComputed) => void;
  onComputed2?: (params: { values: TValues; computed: TComputed }) => void;
}

/** Allows to recalculate computed/dynamic field in Formik form values. */
export default function FormikComputedField<TValues, TComputed>({
  isEnabled = true,
  values,
  deps,
  debouncedDeps,
  throttledDeps,
  compute,
  onComputed,
  onComputed2,
}: FormikComputedFieldProps<TValues, TComputed>) {
  const formikContext = useFormikContext<TValues>();
  // const _values = values || formikContext.values;

  const valuesRef = useLatestRef(values || formikContext.values);
  const prevValuesRef = useRef(valuesRef.current); // initial values or values from prev compute, not prev render
  const isInitialRunRef = useRef(true);
  const computeParamsRef = useLatestRef<ComputeParams<TValues>>({
    values: valuesRef.current,
    prevValues: prevValuesRef.current,
    isInitialRun: isInitialRunRef.current,
  });

  const isEnabledRef = useLatestRef<typeof isEnabled>(
    TypeHelper.isFunction(isEnabled) ? isEnabled(computeParamsRef.current) : isEnabled,
  );
  const computeRef = useLatestRef<typeof compute>(compute);
  const onComputedRef = useLatestRef<typeof onComputed>(onComputed);
  const onComputed2Ref = useLatestRef<typeof onComputed2>(onComputed2);

  // memorize callbacks once
  const handleCompute = useCallback(async (params: ComputeParams<TValues>) => {
    if (isEnabledRef.current) {
      const computed = await computeRef.current(params);
      prevValuesRef.current = valuesRef.current;
      isInitialRunRef.current = false;

      onComputedRef.current && onComputedRef.current(computed);
      onComputed2Ref.current && onComputed2Ref.current({ values: params.values, computed });
    }
  }, []);
  const handleComputeDebounced = useCallback(
    debounce(handleCompute, debouncedDeps?.wait, debouncedDeps?.options),
    [handleCompute],
  );
  const handleComputeThrottled = useCallback(
    throttle(handleCompute, throttledDeps?.wait, throttledDeps?.options),
    [handleCompute],
  );

  useEffectWithDeepCompareIf(
    isEnabledRef.current && !!deps,
    () => {
      handleCompute(computeParamsRef.current);
    },
    [handleCompute, ...(deps || [])],
  );

  useEffectWithDeepCompareIf(
    isEnabledRef.current && !!debouncedDeps,
    () => {
      handleComputeDebounced(computeParamsRef.current);
    },
    [handleComputeDebounced, ...(debouncedDeps?.deps || [])],
  );

  useEffectWithDeepCompareIf(
    isEnabledRef.current && !!throttledDeps,
    () => {
      handleComputeThrottled(computeParamsRef.current);
    },
    [handleComputeThrottled, ...(throttledDeps?.deps || [])],
  );

  return null;
}
